const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');

const sectionLabel = text => ({
    blockType: BlockType.LABEL,
    text
});

class Scratch3MadBotVoice {
    constructor (runtime) {
        this.runtime = runtime;

        // Speech recognition
        this.recognition = null;
        this.recognitionLanguage = 'en-IN';
        this.continuousMode = true;
        this.interimResults = true;

        this.listening = false;
        this.listeningRequested = false;
        this.manualStop = false;
        this.pauseRecognitionForSpeech = false;

        this.status = 'not started';
        this.lastError = '';

        this.lastSpeechText = '';
        this.finalSpeechText = '';
        this.interimSpeechText = '';
        this.confidenceValue = 0;

        this.resultCounter = 0;
        this.lastResultHatCounter = 0;

        this.lastSayHatCounters = {};
        this.lastContainsHatCounters = {};

        // Text to speech
        this.voiceLanguage = 'en-IN';
        this.voiceMode = 'auto';
        this.voiceName = '';
        this.speechRate = 1;
        this.speechPitch = 1;
        this.speechVolume = 1;

        this.speaking = false;
        this.speechToken = 0;
        this.speechStatusText = 'not speaking';
        this.keepSpeechAliveTimer = null;
    }

    getInfo () {
        return {
            id: 'madbotVoice',
            name: 'MadBot Voice',
            color1: '#7C3AED',
            color2: '#6D28D9',
            color3: '#4C1D95',

            blocks: [
                sectionLabel('Listening'),
                {
                    opcode: 'setListening',
                    blockType: BlockType.COMMAND,
                    text: 'set listening [STATE]',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'onOffMenu',
                            defaultValue: 'on'
                        }
                    }
                },
                {
                    opcode: 'isListening',
                    blockType: BlockType.BOOLEAN,
                    text: 'is listening?'
                },
                {
                    opcode: 'isSupported',
                    blockType: BlockType.BOOLEAN,
                    text: 'speech recognition supported?'
                },
                {
                    opcode: 'setRecognitionLanguage',
                    blockType: BlockType.COMMAND,
                    text: 'set recognition language [LANGUAGE]',
                    arguments: {
                        LANGUAGE: {
                            type: ArgumentType.STRING,
                            menu: 'languageMenu',
                            defaultValue: 'en-IN'
                        }
                    }
                },
                {
                    opcode: 'setContinuousMode',
                    blockType: BlockType.COMMAND,
                    text: 'set continuous listening [STATE]',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'onOffMenu',
                            defaultValue: 'on'
                        }
                    }
                },
                {
                    opcode: 'setInterimResults',
                    blockType: BlockType.COMMAND,
                    text: 'set live speech text [STATE]',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'onOffMenu',
                            defaultValue: 'on'
                        }
                    }
                },

                sectionLabel('Recognition'),
                {
                    opcode: 'whenSpeechRecognized',
                    blockType: BlockType.HAT,
                    text: 'when speech recognized',
                    isEdgeActivated: true
                },
                {
                    opcode: 'lastSpeech',
                    blockType: BlockType.REPORTER,
                    text: 'last recognized speech'
                },
                {
                    opcode: 'finalSpeech',
                    blockType: BlockType.REPORTER,
                    text: 'final speech'
                },
                {
                    opcode: 'interimSpeech',
                    blockType: BlockType.REPORTER,
                    text: 'live speech text'
                },
                {
                    opcode: 'confidence',
                    blockType: BlockType.REPORTER,
                    text: 'speech confidence'
                },
                {
                    opcode: 'clearSpeech',
                    blockType: BlockType.COMMAND,
                    text: 'clear speech text'
                },

                sectionLabel('Voice Commands'),
                {
                    opcode: 'whenISay',
                    blockType: BlockType.HAT,
                    text: 'when I say [TEXT]',
                    isEdgeActivated: true,
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: 'forward'
                        }
                    }
                },
                {
                    opcode: 'whenSpeechContains',
                    blockType: BlockType.HAT,
                    text: 'when speech contains [TEXT]',
                    isEdgeActivated: true,
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: 'forward'
                        }
                    }
                },
                {
                    opcode: 'speechEquals',
                    blockType: BlockType.BOOLEAN,
                    text: 'speech equals [TEXT]?',
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: 'forward'
                        }
                    }
                },
                {
                    opcode: 'speechContains',
                    blockType: BlockType.BOOLEAN,
                    text: 'speech contains [TEXT]?',
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: 'forward'
                        }
                    }
                },

                sectionLabel('Speaking'),
                {
                    opcode: 'speakAndWait',
                    blockType: BlockType.COMMAND,
                    text: 'speak [TEXT] and wait',
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: 'Hello, I am MadBot Voice.'
                        }
                    }
                },
                {
                    opcode: 'stopSpeaking',
                    blockType: BlockType.COMMAND,
                    text: 'stop speaking'
                },
                {
                    opcode: 'isSpeaking',
                    blockType: BlockType.BOOLEAN,
                    text: 'is speaking?'
                },
                {
                    opcode: 'ttsSupported',
                    blockType: BlockType.BOOLEAN,
                    text: 'text to speech supported?'
                },

                sectionLabel('Voice Settings'),
                {
                    opcode: 'setVoiceLanguage',
                    blockType: BlockType.COMMAND,
                    text: 'set voice language [LANGUAGE]',
                    arguments: {
                        LANGUAGE: {
                            type: ArgumentType.STRING,
                            menu: 'languageMenu',
                            defaultValue: 'en-IN'
                        }
                    }
                },
                {
                    opcode: 'setVoiceMode',
                    blockType: BlockType.COMMAND,
                    text: 'set voice [VOICE]',
                    arguments: {
                        VOICE: {
                            type: ArgumentType.STRING,
                            menu: 'voiceModeMenu',
                            defaultValue: 'auto'
                        }
                    }
                },
                {
                    opcode: 'setVoiceName',
                    blockType: BlockType.COMMAND,
                    text: 'set voice name [VOICE_NAME]',
                    arguments: {
                        VOICE_NAME: {
                            type: ArgumentType.STRING,
                            defaultValue: 'auto'
                        }
                    }
                },
                {
                    opcode: 'availableVoices',
                    blockType: BlockType.REPORTER,
                    text: 'available voices'
                },
                {
                    opcode: 'setSpeechSpeed',
                    blockType: BlockType.COMMAND,
                    text: 'set speech speed [SPEED]',
                    arguments: {
                        SPEED: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        }
                    }
                },
                {
                    opcode: 'setSpeechPitch',
                    blockType: BlockType.COMMAND,
                    text: 'set speech pitch [PITCH]',
                    arguments: {
                        PITCH: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        }
                    }
                },
                {
                    opcode: 'setSpeechVolume',
                    blockType: BlockType.COMMAND,
                    text: 'set speech volume [VOLUME]',
                    arguments: {
                        VOLUME: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        }
                    }
                },

                sectionLabel('Status'),
                {
                    opcode: 'speechStatus',
                    blockType: BlockType.REPORTER,
                    text: 'voice status'
                },
                {
                    opcode: 'speechError',
                    blockType: BlockType.REPORTER,
                    text: 'voice error'
                }
            ],

            menus: {
                languageMenu: {
                    acceptReporters: true,
                    items: [
                        {
                            text: 'English India',
                            value: 'en-IN'
                        },
                        {
                            text: 'English US',
                            value: 'en-US'
                        },
                        {
                            text: 'English UK',
                            value: 'en-GB'
                        },
                        {
                            text: 'Hindi India',
                            value: 'hi-IN'
                        },
                        {
                            text: 'Gujarati India',
                            value: 'gu-IN'
                        }
                    ]
                },
                onOffMenu: {
                    acceptReporters: true,
                    items: ['on', 'off']
                },
                voiceModeMenu: {
                    acceptReporters: true,
                    items: [
                        {
                            text: 'Auto',
                            value: 'auto'
                        },
                        {
                            text: 'Female voice',
                            value: 'female'
                        },
                        {
                            text: 'Male voice',
                            value: 'male'
                        },
                        {
                            text: 'Google voice',
                            value: 'google'
                        },
                        {
                            text: 'Microsoft voice',
                            value: 'microsoft'
                        },
                        {
                            text: 'First available',
                            value: 'first'
                        }
                    ]
                }
            }
        };
    }

    // -------------------------
    // Listening blocks
    // -------------------------

    setListening (args) {
        const state = String(args.STATE || 'off').toLowerCase();

        if (state === 'on') {
            this.listeningRequested = true;
            this.manualStop = false;
            this.pauseRecognitionForSpeech = false;
            this.startListening();
        } else {
            this.listeningRequested = false;
            this.manualStop = true;
            this.stopListening();
        }
    }

    getRecognitionConstructor () {
        if (typeof window === 'undefined') return null;
        return window.SpeechRecognition || window.webkitSpeechRecognition || null;
    }

    isSupported () {
        return !!this.getRecognitionConstructor();
    }

    createRecognition () {
        const SpeechRecognitionConstructor = this.getRecognitionConstructor();

        if (!SpeechRecognitionConstructor) {
            this.status = 'not supported';
            this.lastError = 'Speech recognition is not supported in this browser. Use Chrome or Edge.';
            return null;
        }

        const recognition = new SpeechRecognitionConstructor();

        recognition.lang = this.recognitionLanguage;
        recognition.continuous = this.continuousMode;
        recognition.interimResults = this.interimResults;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => {
            this.listening = true;
            this.status = 'listening';
            this.lastError = '';
        };

        recognition.onresult = event => {
            this.handleSpeechResult(event);
        };

        recognition.onerror = event => {
            this.listening = false;

            if (event && event.error === 'aborted') {
                return;
            }

            this.status = 'error';
            this.lastError = event && event.error ? event.error : 'unknown error';

            if (this.lastError === 'not-allowed') {
                this.lastError = 'Microphone permission denied.';
                this.listeningRequested = false;
                this.manualStop = true;
            }

            if (this.lastError === 'no-speech') {
                this.status = 'no speech';
            }
        };

        recognition.onend = () => {
            this.listening = false;
            this.recognition = null;

            if (this.status !== 'error' && this.status !== 'no speech') {
                this.status = 'stopped';
            }

            if (
                this.listeningRequested &&
                !this.manualStop &&
                this.continuousMode &&
                !this.pauseRecognitionForSpeech &&
                !this.speaking
            ) {
                setTimeout(() => {
                    if (
                        this.listeningRequested &&
                        !this.manualStop &&
                        this.continuousMode &&
                        !this.pauseRecognitionForSpeech &&
                        !this.speaking
                    ) {
                        this.startListening();
                    }
                }, 500);
            }
        };

        return recognition;
    }

    startListening () {
        if (!this.isSupported()) {
            this.status = 'not supported';
            this.lastError = 'Use Chrome or Edge browser.';
            return;
        }

        if (this.listening || this.recognition) {
            this.status = 'already listening';
            return;
        }

        if (this.speaking) {
            this.status = 'waiting for speaking to finish';
            return;
        }

        try {
            this.manualStop = false;
            this.status = 'starting';

            this.recognition = this.createRecognition();

            if (!this.recognition) return;

            this.recognition.start();
        } catch (error) {
            const message = error && error.message ? error.message : String(error);

            this.listening = false;
            this.status = 'error';

            if (message.toLowerCase().indexOf('already') === -1) {
                this.lastError = message;
            }
        }
    }

    stopListening () {
        this._stopRecognition(false);
    }

    _stopRecognition (temporary) {
        if (!temporary) {
            this.manualStop = true;
            this.listeningRequested = false;
        }

        try {
            if (this.recognition) {
                if (temporary) {
                    this.recognition.abort();
                } else {
                    this.recognition.stop();
                }
            }
        } catch (error) {
            const message = error && error.message ? error.message : String(error);
            if (message.toLowerCase().indexOf('not started') === -1) {
                this.lastError = message;
            }
        }

        this.listening = false;

        if (!temporary) {
            this.status = 'stopped';
        }
    }

    isListening () {
        return !!this.listening;
    }

    setRecognitionLanguage (args) {
        this.recognitionLanguage = String(args.LANGUAGE || 'en-IN');

        if (this.recognition) {
            this.recognition.lang = this.recognitionLanguage;
        }
    }

    setContinuousMode (args) {
        this.continuousMode = String(args.STATE || 'on').toLowerCase() === 'on';

        if (this.recognition) {
            this.recognition.continuous = this.continuousMode;
        }
    }

    setInterimResults (args) {
        this.interimResults = String(args.STATE || 'on').toLowerCase() === 'on';

        if (this.recognition) {
            this.recognition.interimResults = this.interimResults;
        }
    }

    handleSpeechResult (event) {
        let finalText = '';
        let interimText = '';
        let confidenceTotal = 0;
        let confidenceCount = 0;

        for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];

            if (!result || !result[0]) continue;

            const transcript = String(result[0].transcript || '').trim();
            const confidence = Number(result[0].confidence || 0);

            if (result.isFinal) {
                finalText += `${transcript} `;
                confidenceTotal += confidence;
                confidenceCount += 1;
            } else {
                interimText += `${transcript} `;
            }
        }

        finalText = finalText.trim();
        interimText = interimText.trim();

        if (finalText) {
            const avgConfidence = confidenceCount > 0 ? confidenceTotal / confidenceCount : 0;

            this.finalSpeechText = finalText;
            this.lastSpeechText = finalText;
            this.interimSpeechText = '';
            this.confidenceValue = Math.round(avgConfidence * 100);
            this.resultCounter += 1;
            this.status = 'speech recognized';
            this.lastError = '';
        } else if (interimText) {
            this.interimSpeechText = interimText;
            this.lastSpeechText = interimText;
            this.status = 'listening';
        }
    }

    whenSpeechRecognized () {
        if (this.resultCounter !== this.lastResultHatCounter) {
            this.lastResultHatCounter = this.resultCounter;
            return true;
        }

        return false;
    }

    lastSpeech () {
        return this.lastSpeechText || '';
    }

    finalSpeech () {
        return this.finalSpeechText || '';
    }

    interimSpeech () {
        return this.interimSpeechText || '';
    }

    confidence () {
        return this.confidenceValue || 0;
    }

    clearSpeech () {
        this.lastSpeechText = '';
        this.finalSpeechText = '';
        this.interimSpeechText = '';
        this.confidenceValue = 0;
        this.lastError = '';
        this.status = this.listening ? 'listening' : 'cleared';
    }

    // -------------------------
    // Voice command blocks
    // -------------------------

    whenISay (args) {
        const target = this.normalizeText(args.TEXT);
        const speech = this.normalizeText(this.finalSpeechText);

        if (!target || !speech) return false;

        const key = `say:${target}`;
        const lastCounter = this.lastSayHatCounters[key] || 0;

        if (speech === target && this.resultCounter !== lastCounter) {
            this.lastSayHatCounters[key] = this.resultCounter;
            return true;
        }

        return false;
    }

    whenSpeechContains (args) {
        const target = this.normalizeText(args.TEXT);
        const speech = this.normalizeText(this.finalSpeechText);

        if (!target || !speech) return false;

        const key = `contains:${target}`;
        const lastCounter = this.lastContainsHatCounters[key] || 0;

        if (speech.indexOf(target) !== -1 && this.resultCounter !== lastCounter) {
            this.lastContainsHatCounters[key] = this.resultCounter;
            return true;
        }

        return false;
    }

    speechEquals (args) {
        const target = this.normalizeText(args.TEXT);
        const speech = this.normalizeText(this.finalSpeechText || this.lastSpeechText);

        return !!target && speech === target;
    }

    speechContains (args) {
        const target = this.normalizeText(args.TEXT);
        const speech = this.normalizeText(this.finalSpeechText || this.lastSpeechText);

        return !!target && speech.indexOf(target) !== -1;
    }

    // -------------------------
    // Text to speech blocks
    // -------------------------

    ttsSupported () {
        return this._hasSpeechSynthesis();
    }

    async speakAndWait (args) {
        let text = String(args.TEXT || '').trim();

        if (!text) {
            this.lastError = 'No text to speak.';
            return;
        }

        if (!this._hasSpeechSynthesis()) {
            this.lastError = 'Text to speech is not supported in this browser.';
            this.speechStatusText = 'not supported';
            return;
        }

        text = this._cleanTextForSpeech(text);

        if (!text) {
            this.lastError = 'No valid text to speak.';
            return;
        }

        const shouldResumeListening = this.listeningRequested && !this.manualStop;

        if (this.listening || this.recognition) {
            this.pauseRecognitionForSpeech = true;
            this._stopRecognition(true);
        }

        this.speechToken += 1;
        const currentToken = this.speechToken;

        this._cancelSpeechOnly();

        this.speaking = true;
        this.speechStatusText = 'speaking';
        this.status = 'speaking';
        this.lastError = '';

        try {
            await this._waitForVoices();

            const chunks = this._splitTextIntoChunks(text, 160);

            this._startSpeechKeepAlive();

            for (let i = 0; i < chunks.length; i++) {
                if (currentToken !== this.speechToken) break;

                await this._speakChunk(chunks[i], currentToken);
            }
        } catch (error) {
            this.lastError = error && error.message ? error.message : 'Speech failed.';
        } finally {
            this._stopSpeechKeepAlive();

            if (currentToken === this.speechToken) {
                this.speaking = false;
                this.speechStatusText = 'finished';
                this.status = 'speech finished';
            }

            this.pauseRecognitionForSpeech = false;

            if (shouldResumeListening && this.listeningRequested && !this.manualStop) {
                setTimeout(() => {
                    if (
                        this.listeningRequested &&
                        !this.manualStop &&
                        !this.speaking
                    ) {
                        this.startListening();
                    }
                }, 500);
            }
        }
    }

    stopSpeaking () {
        this.speechToken += 1;
        this.speaking = false;
        this.speechStatusText = 'stopped';
        this._stopSpeechKeepAlive();
        this._cancelSpeechOnly();
    }

    isSpeaking () {
        return !!this.speaking;
    }

    setVoiceLanguage (args) {
        this.voiceLanguage = String(args.LANGUAGE || 'en-IN');
    }

    setVoiceMode (args) {
        this.voiceMode = String(args.VOICE || 'auto').toLowerCase();

        if (this.voiceMode === 'auto') {
            this.voiceName = '';
        }
    }

    setVoiceName (args) {
        const name = String(args.VOICE_NAME || '').trim();

        if (!name || name.toLowerCase() === 'auto') {
            this.voiceName = '';
            this.voiceMode = 'auto';
        } else {
            this.voiceName = name;
        }
    }

    availableVoices () {
        if (!this._hasSpeechSynthesis()) {
            return 'Text to speech is not supported.';
        }

        const voices = window.speechSynthesis.getVoices();

        if (!voices || voices.length === 0) {
            window.speechSynthesis.getVoices();
            return 'Voices are loading. Try again after few seconds.';
        }

        return voices
            .map(voice => `${voice.name} (${voice.lang})`)
            .slice(0, 40)
            .join(', ');
    }

    setSpeechSpeed (args) {
        const speed = Number(args.SPEED);

        if (Number.isNaN(speed)) {
            this.speechRate = 1;
            return;
        }

        this.speechRate = Math.min(Math.max(speed, 0.5), 2);
    }

    setSpeechPitch (args) {
        const pitch = Number(args.PITCH);

        if (Number.isNaN(pitch)) {
            this.speechPitch = 1;
            return;
        }

        this.speechPitch = Math.min(Math.max(pitch, 0), 2);
    }

    setSpeechVolume (args) {
        const volume = Number(args.VOLUME);

        if (Number.isNaN(volume)) {
            this.speechVolume = 1;
            return;
        }

        this.speechVolume = Math.min(Math.max(volume, 0), 1);
    }

    _speakChunk (text, token) {
        return new Promise(resolve => {
            if (!text || token !== this.speechToken) {
                resolve();
                return;
            }

            const utterance = new SpeechSynthesisUtterance(text);
            const voice = this._getBestVoice();

            utterance.lang = this.voiceLanguage;
            utterance.rate = this.speechRate;
            utterance.pitch = this.speechPitch;
            utterance.volume = this.speechVolume;

            if (voice) {
                utterance.voice = voice;
            }

            let finished = false;

            const finish = () => {
                if (finished) return;
                finished = true;
                resolve();
            };

            utterance.onend = finish;

            utterance.onerror = event => {
                if (
                    event &&
                    event.error &&
                    event.error !== 'interrupted' &&
                    event.error !== 'canceled'
                ) {
                    this.lastError = `Speech error: ${event.error}`;
                }

                finish();
            };

            const safetyTime = Math.min(Math.max(text.length * 140, 8000), 30000);

            setTimeout(finish, safetyTime);

            try {
                window.speechSynthesis.speak(utterance);
            } catch (error) {
                this.lastError = error && error.message ? error.message : 'Speech failed.';
                finish();
            }
        });
    }

    _getBestVoice () {
        if (!this._hasSpeechSynthesis()) return null;

        const voices = window.speechSynthesis.getVoices();

        if (!voices || voices.length === 0) return null;

        const targetLang = String(this.voiceLanguage || '').toLowerCase();
        const shortLang = targetLang.split('-')[0];

        const langVoices = voices.filter(voice => {
            const lang = String(voice.lang || '').toLowerCase();
            return lang === targetLang || lang.indexOf(shortLang) === 0;
        });

        const candidates = langVoices.length > 0 ? langVoices : voices;

        if (this.voiceName) {
            const exactVoice = voices.find(voice =>
                String(voice.name || '').toLowerCase() === this.voiceName.toLowerCase()
            );

            if (exactVoice) return exactVoice;

            const partialVoice = voices.find(voice =>
                String(voice.name || '').toLowerCase().indexOf(this.voiceName.toLowerCase()) !== -1
            );

            if (partialVoice) return partialVoice;
        }

        const mode = String(this.voiceMode || 'auto').toLowerCase();

        if (mode === 'first') {
            return candidates[0] || voices[0] || null;
        }

        if (mode === 'google') {
            const googleVoice = candidates.find(voice =>
                String(voice.name || '').toLowerCase().indexOf('google') !== -1
            );
            if (googleVoice) return googleVoice;
        }

        if (mode === 'microsoft') {
            const microsoftVoice = candidates.find(voice =>
                String(voice.name || '').toLowerCase().indexOf('microsoft') !== -1
            );
            if (microsoftVoice) return microsoftVoice;
        }

        if (mode === 'female') {
            const femaleVoice = candidates.find(voice => {
                const name = String(voice.name || '').toLowerCase();
                return name.indexOf('female') !== -1 ||
                    name.indexOf('woman') !== -1 ||
                    name.indexOf('zira') !== -1 ||
                    name.indexOf('heera') !== -1 ||
                    name.indexOf('kalpana') !== -1 ||
                    name.indexOf('swara') !== -1 ||
                    name.indexOf('neerja') !== -1 ||
                    name.indexOf('jenny') !== -1 ||
                    name.indexOf('aria') !== -1;
            });

            if (femaleVoice) return femaleVoice;
        }

        if (mode === 'male') {
            const maleVoice = candidates.find(voice => {
                const name = String(voice.name || '').toLowerCase();
                return name.indexOf('male') !== -1 ||
                    name.indexOf('man') !== -1 ||
                    name.indexOf('ravi') !== -1 ||
                    name.indexOf('kabir') !== -1 ||
                    name.indexOf('david') !== -1 ||
                    name.indexOf('mark') !== -1 ||
                    name.indexOf('guy') !== -1;
            });

            if (maleVoice) return maleVoice;
        }

        const exactLangVoice = voices.find(voice =>
            String(voice.lang || '').toLowerCase() === targetLang
        );

        if (exactLangVoice) return exactLangVoice;

        return candidates[0] || voices[0] || null;
    }

    _waitForVoices () {
        return new Promise(resolve => {
            if (!this._hasSpeechSynthesis()) {
                resolve();
                return;
            }

            const voices = window.speechSynthesis.getVoices();

            if (voices && voices.length > 0) {
                resolve();
                return;
            }

            let done = false;

            const finish = () => {
                if (done) return;
                done = true;
                resolve();
            };

            window.speechSynthesis.onvoiceschanged = finish;
            setTimeout(finish, 1200);
        });
    }

    _splitTextIntoChunks (text, maxLength) {
        const chunks = [];
        const sentences = text.match(/[^.!?।]+[.!?।]*/g) || [text];

        let current = '';

        for (let i = 0; i < sentences.length; i++) {
            const sentence = String(sentences[i] || '').trim();

            if (!sentence) continue;

            const next = `${current} ${sentence}`.trim();

            if (next.length <= maxLength) {
                current = next;
            } else {
                if (current) {
                    chunks.push(current);
                    current = '';
                }

                if (sentence.length <= maxLength) {
                    current = sentence;
                } else {
                    const smallerChunks = this._splitLongSentence(sentence, maxLength);

                    for (let j = 0; j < smallerChunks.length; j++) {
                        chunks.push(smallerChunks[j]);
                    }
                }
            }
        }

        if (current) {
            chunks.push(current);
        }

        return chunks;
    }

    _splitLongSentence (text, maxLength) {
        const words = String(text || '').split(/\s+/);
        const chunks = [];
        let current = '';

        for (let i = 0; i < words.length; i++) {
            const word = words[i];
            const next = `${current} ${word}`.trim();

            if (next.length <= maxLength) {
                current = next;
            } else {
                if (current) {
                    chunks.push(current);
                }

                current = word;
            }
        }

        if (current) {
            chunks.push(current);
        }

        return chunks;
    }

    _cleanTextForSpeech (text) {
        let cleaned = String(text || '');

        cleaned = cleaned.replace(/<[^>]*>/g, ' ');
        cleaned = cleaned.replace(/\*\*/g, '');
        cleaned = cleaned.replace(/[`#*_~>]/g, '');
        cleaned = cleaned.replace(/[\r\n]+/g, '. ');
        cleaned = cleaned.replace(/\s+/g, ' ');
        cleaned = cleaned.replace(/\s+\./g, '.');
        cleaned = cleaned.replace(/\.{2,}/g, '.');
        cleaned = cleaned.trim();

        return cleaned;
    }

    _hasSpeechSynthesis () {
        return typeof window !== 'undefined' &&
            window.speechSynthesis &&
            window.SpeechSynthesisUtterance;
    }

    _startSpeechKeepAlive () {
        this._stopSpeechKeepAlive();

        if (!this._hasSpeechSynthesis()) return;

        this.keepSpeechAliveTimer = setInterval(() => {
            try {
                if (window.speechSynthesis.paused) {
                    window.speechSynthesis.resume();
                }
            } catch (e) {
                // Ignore resume errors.
            }
        }, 7000);
    }

    _stopSpeechKeepAlive () {
        if (this.keepSpeechAliveTimer) {
            clearInterval(this.keepSpeechAliveTimer);
            this.keepSpeechAliveTimer = null;
        }
    }

    _cancelSpeechOnly () {
        if (!this._hasSpeechSynthesis()) return;

        try {
            window.speechSynthesis.cancel();
        } catch (e) {
            // Ignore cancel errors.
        }
    }

    // -------------------------
    // Status blocks
    // -------------------------

    speechStatus () {
        if (this.speaking) return this.speechStatusText || 'speaking';
        return this.status || '';
    }

    speechError () {
        return this.lastError || '';
    }

    normalizeText (text) {
        return String(text || '')
            .toLowerCase()
            .replace(/[.,!?;:।]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    }
}

module.exports = Scratch3MadBotVoice;